import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import {
  Fade, IconButton, Button, Dialog, DialogContent, DialogActions,
} from '@material-ui/core';
import { ArrowBack, Clear } from '@material-ui/icons';
import {
  get, getOr, isArray, map, reject,
} from 'lodash/fp';
import qs from 'query-string';

import { Snackbar, Spinner, RetryDialog } from '../../../components';
import {
  isWebView, closeWebView, disableZoom, enableZoom,
} from '../../../utils';
import * as newslettersActions from '../../newsletters/actions';
import * as actions from '../actions';
import { Information, ResponseForm } from '../components';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    background-color: #eaecee;
  `,
  ActionBar: styled.header`
    display: flex;
    align-items: center;
    min-height: 56px;
    margin-bottom: 1px;
    padding: 0 12px;
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  Heading: styled.h2`
    margin: 0 auto 0 32px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.2;
  `,
  ArrowBack: styled(ArrowBack)`
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Clear: styled(Clear)`
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Information: styled(Information)`
    margin-bottom: 1px;
  `,
  Scrollable: styled.div`
    flex-grow: 1;
    overflow: auto;
    /* XXX: 터치 스크롤 적용 시 버튼이 숨겨지는 문제로 인해 일시적으로 주석 처리 */
    /* -webkit-overflow-scrolling: touch; */
    margin-bottom: 68px;
  `,
};

const Newsletter = ({ location, match }) => {
  const [error, setError] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorPdfDialogOpen, setErrorPdfDialogOpen] = useState(false);

  const favorites = useSelector((state) => state.newsletters.favorites, shallowEqual);
  const favoritesUpdated = useSelector((state) => state.newsletters.favoritesUpdated, shallowEqual);
  const newsletter = useSelector((state) => state.newsletter.newsletter, shallowEqual);
  const survey = useSelector((state) => state.newsletter.survey, shallowEqual);
  const individual = useSelector((state) => state.newsletter.individual, shallowEqual);
  const target = useSelector((state) => state.newsletter.target, shallowEqual);
  const response = useSelector((state) => state.newsletter.response, shallowEqual);

  const dispatch = useDispatch();

  const fileUrl = useMemo(() => (get('blob', newsletter.data)
    ? URL.createObjectURL(newsletter.data.blob) : ''), [newsletter.data]);

  const recorded = useMemo(() => {
    const gcid = get('gcid', qs.parse(location.search));

    return get(`targets.${gcid}`, newsletter.data);
  }, [location.search, newsletter.data]);

  const hasNoSurvey = useMemo(() => !get('questions', survey.data) && !get('signatures', survey.data), [survey.data]);

  const createResponse = useCallback((...args) => dispatch(actions.createResponseRequest(...args)), [dispatch]);
  const updateResponse = useCallback((...args) => dispatch(actions.updateResponseRequest(...args)), [dispatch]);
  const updateFavorites = useCallback((event) => dispatch(newslettersActions.updateFavoritesRequest({
    value: event.currentTarget.dataset.value,
  })), [dispatch]);

  const handleSubmit = useCallback((values) => (get('answers', individual.data)
    ? updateResponse({
      answers: map((answer) => {
        if (isArray(answer)) {
          return reject((val) => val === 'other:', answer);
        }
        return answer === 'other:' ? '' : answer;
      }, values.answers),
      signatures: values.signatures,
      nid: match.params.nid,
      gcid: get('gcid', qs.parse(location.search)),
    })
    : createResponse({
      answers: map((answer) => {
        if (isArray(answer)) {
          return reject((val) => val === 'other:', answer);
        }
        return answer === 'other:' ? '' : answer;
      }, values.answers),
      signatures: values.signatures,
      nid: match.params.nid,
      gcid: get('gcid', qs.parse(location.search)),
    })
  ), [individual.data, updateResponse, match.params.nid, location.search, createResponse]);

  const handleErrorPdf = useCallback(() => {
    setErrorPdfDialogOpen(true);
  }, []);

  const handleExited = () => dispatch(actions.initResponse());

  const handleErrorDialogRetry = () => window.location.reload();
  const handleErrorDialogClose = () => setErrorDialogOpen(false);

  const handlePdfErrorDialogClose = () => setErrorPdfDialogOpen(false);

  useEffect(() => {
    const { gcid } = qs.parse(location.search);

    dispatch(actions.fetchTargetRequest({ gcid }));
  }, [dispatch, location.search]);

  useEffect(() => {
    if (!target.data) {
      return;
    }

    const { gcid } = qs.parse(location.search);
    const { nid, sid } = match.params;

    dispatch(actions.fetchNewsletterRequest({ nid, sid }));
    dispatch(actions.fetchSurveyRequest({ nid }));
    dispatch(actions.fetchIndividualRequest({ nid, gcid }));
    dispatch(newslettersActions.fetchFavoritesRequest({ gcid }));
  }, [dispatch, location.search, match.params, target.data]);

  useEffect(() => () => URL.revokeObjectURL(fileUrl), [fileUrl]);

  useEffect(() => () => {
    dispatch(actions.initNewsletter());
    dispatch(actions.initTarget());
    dispatch(actions.initIndividual());
  }, [dispatch]);

  useEffect(() => {
    if (isWebView()) {
      enableZoom();

      return disableZoom;
    }

    return () => {};
  }, []);

  useEffect(() => {
    if (recorded === false && hasNoSurvey && target.data) {
      createResponse({
        answers: null,
        signatures: null,
        nid: match.params.nid,
        gcid: get('gcid', qs.parse(location.search)),
      });
    }
  }, [createResponse, hasNoSurvey, location.search, match.params.nid, recorded, target.data]);

  useEffect(() => {
    if (response.data && hasNoSurvey) {
      dispatch(actions.initResponse());
    }
  }, [dispatch, hasNoSurvey, response.data]);

  useEffect(() => {
    if (response.error) {
      setError(true);

      const timeoutId = setTimeout(() => setError(false), 3500);

      return () => clearTimeout(timeoutId);
    }

    return () => {};
  }, [dispatch, response.error]);

  useEffect(() => {
    if (newsletter.error || survey.error || individual.error || target.error) {
      setErrorDialogOpen(true);
    }
  }, [
    newsletter.error,
    survey.error,
    individual.error,
    target.error,
  ]);

  useEffect(() => () => {
    dispatch(actions.clearNewsletterError());
    dispatch(actions.clearSurveyError());
    dispatch(actions.clearIndividualError());
    dispatch(actions.clearTargetError());
  }, [dispatch]);

  if (get('error.response', target)
      && (target.error.response.status === 403 || target.error.response.status === 404)) {
    return {
      403: (
        <Redirect
          to={{
            pathname: '/connection',
            state: { from: location.pathname },
            search: `?${qs.stringify({
              gcid: getOr('', 'gcid', qs.parse(location.search)),
              token: getOr('', 'token', qs.parse(location.search)),
            })}`,
          }}
        />
      ),
      404: (
        <Redirect
          to={{
            pathname: '/school-newsletters',
            state: { from: location.pathname },
          }}
        />
      ),
    }[target.error.response.status];
  }

  if (response.data && !hasNoSurvey) {
    return <Redirect to={{ pathname: '/school-newsletters', state: { from: location.pathname } }} />;
  }

  if (newsletter.error && newsletter.error.code === 'storage/object-not-found') {
    return <Redirect to={{ pathname: '/not-found', state: { from: location.pathname } }} />;
  }

  return (
    <>
      <S.Wrapper>
        <S.ActionBar>
          <IconButton
            component={Link}
            to={{
              pathname: '/school-newsletters',
              state: { from: location.pathname },
            }}
            size="small"
          >
            <S.ArrowBack />
          </IconButton>
          <S.Heading>가정통신문 상세 보기</S.Heading>
          {isWebView() && (
            <IconButton type="button" size="small" onClick={closeWebView}>
              <S.Clear />
            </IconButton>
          )}
        </S.ActionBar>
        {(favorites.loading || newsletter.loading || survey.loading || individual.loading || target.loading)
          ? <Spinner />
          : target.data && (
            <S.Scrollable>
              <S.Information
                favorites={favorites}
                hasNoSurvey={hasNoSurvey}
                newsletter={newsletter}
                target={target}
                onUpdate={updateFavorites}
              />
              <ResponseForm
                fileUrl={fileUrl}
                hasNoSurvey={hasNoSurvey}
                individual={individual}
                newsletter={newsletter}
                survey={survey}
                onSubmit={handleSubmit}
                onErrorPdf={handleErrorPdf}
              />
            </S.Scrollable>
          )}
      </S.Wrapper>
      {(favoritesUpdated.loading || response.loading) && <Spinner />}
      <Snackbar
        open={error}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'response-error-message',
        }}
        message={(
          <span id="response-error-message">
            {get('message', response.error) || '응답 제출에 실패하였습니다'}
          </span>
        )}
        onExited={handleExited}
      />

      <RetryDialog open={errorDialogOpen} onRetry={handleErrorDialogRetry} onClose={handleErrorDialogClose}>
        정보를 불러오는데 실패하였습니다. 다시 시도해주세요.
      </RetryDialog>
      <Dialog
        open={errorPdfDialogOpen}
        onClose={handlePdfErrorDialogClose}
      >
        <DialogContent>
          가정통신문을 불러오는데 실패했습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePdfErrorDialogClose} color="primary">
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

Newsletter.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      nid: PropTypes.string.isRequired,
      sid: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default Newsletter;
